<script lang="ts" setup>
import {
  computed, ref, onMounted, onBeforeUnmount, watch,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { ElLink } from 'element-plus';
import { Search, Calendar } from '@element-plus/icons-vue';
import { type ActivityLogRow } from 'src/data/types';
import ActivityDetails from '@/views/activity/ActivityDetails/ActivityDetails.vue';
import {
  getSocialShopProfilePath,
  getSocialShopSourceLabel,
} from '@/views/activity/socialShopAttribution';

const store = useStore();
const route = useRoute();

const selectedSort = computed(() => store.getters['activityLogs/GET_SORT']);

const isSortable = (sortableType: string) => sortableType === selectedSort.value;

// Use new store structure for current transaction data
const logs = computed(() => store.getters['activityLogs/GET_CURRENT_TRANSACTIONS']);
const logsLoading = computed(() => store.getters['activityLogs/GET_CURRENT_LOADING']);
const logsError = computed(() => store.getters['activityLogs/GET_CURRENT_ERROR']);
const currentTab = computed(() => store.getters['activityLogs/GET_CURRENT_TAB']);

const currentPage = ref(1);
const rowsPerPage = ref(100);
const totalRows = ref(0);

const tableData = ref<ActivityLogRow[]>([]);

// Watch for changes in current transaction data
watch(logs, (newLogs) => {
  if (!newLogs || newLogs.length === 0) {
    tableData.value = [];
  } else {
    tableData.value = newLogs.map((item: any) => ({
      ...item,
      checked: false,
    }));
  }
}, { immediate: true });

// Watch for tab changes to reset table data
watch(currentTab, () => {
  tableData.value = [];
  currentPage.value = 1;
}, { immediate: true });

watch(() => route.params.id, () => {
  tableData.value = [];
  currentPage.value = 1;
}, { immediate: true });

const filteredTableData = computed(() => {
  // Use the store getter for filtered transactions to ensure consistency with export
  const filteredFromStore = store.getters['activityLogs/GET_FILTERED_TRANSACTIONS'];

  // Map to include the checked state from tableData
  const filteredData = filteredFromStore.map((item) => {
    const existingRow = tableData.value.find((row) => row.transactionId === item.transactionId);
    return {
      ...item,
      checked: existingRow?.checked || false,
    };
  });

  // eslint-disable-next-line vue/no-side-effects-in-computed-properties -- TODO(lint): move to a watcher
  totalRows.value = filteredData.length;
  return filteredData;
});

// Handle pagination changes - now local pagination
const handlePageChange = (page: number) => {
  currentPage.value = page;
};

const handlePageSizeChange = (size: number) => {
  rowsPerPage.value = size;
  currentPage.value = 1;
};

// Calculate paginated data for local pagination
const paginatedTableData = computed(() => {
  const startIndex = (currentPage.value - 1) * rowsPerPage.value;
  const endIndex = startIndex + rowsPerPage.value;
  return filteredTableData.value.slice(startIndex, endIndex);
});

const deleteSearchAndFilters = () => {
  store.dispatch('activityLogs/SET_SEARCH', (''));
  store.dispatch('activityLogs/SET_SORT', ('transaction'));
  // Reset to default last 30 days instead of clearing
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 30);
  store.dispatch('activityLogs/SET_DATE', ([startDate, endDate]));
};

const rowsSelected = computed(() => {
  let selected = 0;
  const selectedRows: ActivityLogRow[] = [];

  tableData.value.forEach((row) => {
    if (row.checked) {
      selected++;
      selectedRows.push(row);
    }
  });
  store.dispatch('activityLogs/SET_SELECTED_LOGS', selectedRows);
  return selected;
});

const onSelectAllRows = () => {
  tableData.value.forEach((row) => {
    row.checked = true;
  });
};

const onUnselectAllRows = () => {
  tableData.value.forEach((row) => {
    row.checked = false;
  });
};

const hasSelectedOnPage = computed(() => filteredTableData?.value.length > 0 && filteredTableData.value.some((row) => row.checked));

const isAllSelectedOnPage = computed({
  get() {
    return filteredTableData.value.length > 0 && filteredTableData.value.every((row) => row.checked);
  },
  set(value: boolean) {
    filteredTableData.value.forEach((row) => {
      row.checked = value;
    });
  },
});

const resizeHeader = () => {
  let elapsedTime = 0;
  const maxTime = 10000;
  const intervalId = setInterval(() => {
    elapsedTime += 100;

    const headerElement = document.querySelector('.el-table__header');
    if (headerElement) {
      const headerHeight = headerElement.clientHeight - 2;
      const selectAllElement = document.querySelector('.c-activity-table__select-all');
      if (selectAllElement) {
        (selectAllElement as HTMLElement).style.height = `${headerHeight}px`;
        clearInterval(intervalId);
      }
    }

    if (elapsedTime >= maxTime) {
      clearInterval(intervalId);
    }
  }, 100);

  onBeforeUnmount(() => {
    clearInterval(intervalId);
  });
};

const showDetails = ref(false);
const detailsTransactionId = ref();
const onOpenDetails = (row: ActivityLogRow) => {
  const { transactionId } = row;
  showDetails.value = true;
  detailsTransactionId.value = transactionId;
};

const getInitials = (name: string) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getTypeBadgeClass = (type: string) => {
  const typeMap: { [key: string]: string } = {
    reward: 'c-activity-badge--reward',
    payment: 'c-activity-badge--payment',
    'peer transfer': 'c-activity-badge--peer-transfer',
  };
  return typeMap[type?.toLowerCase()] || 'c-activity-badge--default';
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

const formatDetails = (details: string) => {
  if (!details) return '';
  
  // Remove "Cause name:" if there's nothing after it (empty or just whitespace)
  // Pattern: "Cause name:" followed by optional whitespace and nothing else or end of string
  return details.replace(/Cause name:\s*$/i, '').trim();
};

const handleSortChange = ({ prop, order }) => {
  if (order === null) {
    tableData.value = [...logs.value].map((item) => ({
      ...item,
      checked: false,
    }));
  } else if (prop && order) {
    tableData.value.sort((a, b) => {
      let aValue; let
        bValue;
      const orderMultiplier = order === 'ascending' ? 1 : -1;
      if (prop === 'amount') {
        aValue = a.amount;
        bValue = b.amount;
        return (aValue - bValue) * orderMultiplier;
      } if (prop === 'date') {
        aValue = new Date(a.date);
        bValue = new Date(b.date);
        return (aValue - bValue) * orderMultiplier;
      } if (prop === 'userName' || prop === 'transactionId' || prop === 'details' || prop === 'email' || prop === 'item' || prop === 'type' || prop === 'embedOrganizationId' || prop === 'embedOrganizationUserName' || prop === 'embedOrganizationUserEmail' || prop === 'embedOrganizationHost') {
        aValue = a[prop] ? a[prop].toLowerCase() : '';
        bValue = b[prop] ? b[prop].toLowerCase() : '';
        return aValue.localeCompare(bValue) * orderMultiplier;
      } if (prop === 'isAnonymous' || prop === 'isProcessed') {
        aValue = a[prop] ? 1 : 0;
        bValue = b[prop] ? 1 : 0;
        return (aValue - bValue) * orderMultiplier;
      }
      return 0;
    });
  }
};

onMounted(async () => {
  // Fetch data for the current tab with date range
  await store.dispatch('activityLogs/FETCH_CURRENT_TAB_DATA');
  await resizeHeader();
});

</script>

<template>
  <section class="c-activity-table">
    <template v-if="logsLoading">
      <div class="c-skeleton-loader">
        <div class="c-skeleton-row" v-for="n in 10" :key="n">
          <div class="c-skeleton-cell c-skeleton-cell--checkbox"></div>
          <div class="c-skeleton-cell c-skeleton-cell--transaction"></div>
          <div class="c-skeleton-cell c-skeleton-cell--date"></div>
          <div class="c-skeleton-cell c-skeleton-cell--user"></div>
          <div class="c-skeleton-cell c-skeleton-cell--email"></div>
          <div class="c-skeleton-cell c-skeleton-cell--amount"></div>
          <div class="c-skeleton-cell c-skeleton-cell--item"></div>
          <div class="c-skeleton-cell c-skeleton-cell--details"></div>
          <div class="c-skeleton-cell c-skeleton-cell--type"></div>
          <div class="c-skeleton-cell c-skeleton-cell--anonymous"></div>
        </div>
      </div>
    </template>
    <section v-else-if="!logsLoading && logsError" class="error-message">
      <div class="error-icon">&#9888;</div>
      <div class="error-text">
        <h2>Oops! Something went wrong.</h2>
        <p>We encountered an error. <br /> Please try refreshing the page or come back later.</p>
      </div>
    </section>
    <template v-else>

      <article class="c-activity-table__select-all" :class="{ 'c-activity-table__select-all--small': rowsSelected === 0 }">
        <el-checkbox :indeterminate="hasSelectedOnPage && !isAllSelectedOnPage" class="mr-2" v-model="isAllSelectedOnPage" />
        <!--        <template v-show="rowsSelected > 0">-->
        <div class="fs-12 fw-medium">{{ rowsSelected }} selected</div>
        <el-link v-if="rowsSelected !== tableData.length" @click="onSelectAllRows" class="text-success fw-bold fs-10">Select All</el-link>
        <el-link v-else @click="onUnselectAllRows" class="text-success fw-bold fs-10">Unselect All</el-link>
        <!--        </template>-->
      </article>
      <el-table
        id="activity-table"
        :data="paginatedTableData"
        ref="activityTable"
        :default-sort="{ prop: selectedSort.value, order: 'descending' }"
        @row-click="onOpenDetails"
        @sort-change="handleSortChange"
      >
        <el-table-column width="40">
          <template #default="scope">
            <el-checkbox :value="scope.row.checked" v-model="scope.row.checked" @click.stop />
          </template>
        </el-table-column>

        <el-table-column :sortable="isSortable('transaction')" label="Transaction ID" prop="transactionId" width="160">
          <template #default="scope">
            <div class="c-activity-table__transaction-id">
              #{{ scope.row.transactionId.slice(-5) }}
            </div>
          </template>
        </el-table-column>
        <el-table-column :sortable="isSortable('date')" label="Date" prop="date" width="150">
          <template #default="scope">
            <div class="c-activity-table__date">
              <el-icon class="c-activity-table__date-icon">
                <Calendar />
              </el-icon>
              <span>{{ formatDate(scope.row.date) }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column :sortable="isSortable('user')" label="User" prop="userName" width="180">
          <template #default="scope">
            <div class="c-activity-table__user">
              <div class="c-activity-table__avatar">
                {{ getInitials(scope.row.userName) }}
              </div>
              <span class="c-activity-table__user-name">{{ scope.row.userName }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column :sortable="isSortable('email')" label="Email" prop="email" width="200" />
        <el-table-column :sortable="isSortable('total')" label="Amount" prop="amount" width="120" align="right" header-align="right">

          <template #default="scope">
            <span v-if="scope.row.amount === 0" class="c-activity-table__amount c-activity-table__amount--zero">—</span>
            <span v-else class="c-activity-table__amount">${{ scope.row.amount.toFixed(2) }}</span>
          </template>
        </el-table-column>
        <el-table-column :sortable="isSortable('item')" label="Item" prop="item" width="180">
          <template #default="scope">
            <div class="c-activity-table__item">
              {{ scope.row.item }}
            </div>
          </template>
        </el-table-column>
        <el-table-column :sortable="isSortable('details')" label="Details" prop="details" width="280">
          <template #default="scope">
            <div class="c-activity-table__details">
              {{ formatDetails(scope.row.details) }}
            </div>
          </template>
        </el-table-column>
        <el-table-column
          v-if="currentTab === 'sticker'"
          label="Shop Source"
          prop="socialShopName"
          width="180"
        >
          <template #default="scope">
            <template v-if="getSocialShopSourceLabel(scope.row)">
              <router-link
                v-if="getSocialShopProfilePath(scope.row)"
                :to="getSocialShopProfilePath(scope.row)!"
                class="c-activity-table__shop-link"
                @click.stop
              >
                {{ getSocialShopSourceLabel(scope.row) }}
              </router-link>
              <span v-else class="c-activity-table__shop-source">
                {{ getSocialShopSourceLabel(scope.row) }}
              </span>
            </template>
            <span v-else class="text-muted">-</span>
          </template>
        </el-table-column>
        <el-table-column :sortable="isSortable('type')" label="Type" prop="type" width="140">
          <template #default="scope">
            <span class="c-activity-badge" :class="getTypeBadgeClass(scope.row.type)">
              {{ scope.row.type }}
            </span>
          </template>
        </el-table-column>
        <el-table-column sortable label="Anonymous" prop="isAnonymous" width="140">
          <template #default="scope">
            <span
              class="c-activity-badge"
              :class="scope.row.isAnonymous ? 'c-activity-badge--anonymous' : 'c-activity-badge--none'"
            >
              {{ scope.row.isAnonymous ? 'Yes' : 'No' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column sortable label="Embed Org ID" prop="embedOrganizationId" width="150">
          <template #default="scope">
            <div class="text-truncate" style="max-width: 130px;" :title="scope.row.embedOrganizationId">
              {{ scope.row.embedOrganizationId || '-' }}
            </div>
          </template>
        </el-table-column>
        <el-table-column sortable label="Embed User" prop="embedOrganizationUserName" width="150">
          <template #default="scope">
            <div class="text-truncate" style="max-width: 130px;" :title="scope.row.embedOrganizationUserName">
              {{ scope.row.embedOrganizationUserName || '-' }}
            </div>
          </template>
        </el-table-column>
        <el-table-column sortable label="Embed Email" prop="embedOrganizationUserEmail" width="180">
          <template #default="scope">
            <div class="text-truncate" style="max-width: 160px;" :title="scope.row.embedOrganizationUserEmail">
              {{ scope.row.embedOrganizationUserEmail || '-' }}
            </div>
          </template>
        </el-table-column>
        <el-table-column sortable label="Embed Host" prop="embedOrganizationHost" width="150">
          <template #default="scope">
            <div class="text-truncate" style="max-width: 130px;" :title="scope.row.embedOrganizationHost">
              {{ scope.row.embedOrganizationHost || '-' }}
            </div>
          </template>
        </el-table-column>
        <el-table-column sortable label="Processed" prop="isProcessed" width="140">
          <template #default="scope">
            <span
              class="c-activity-badge"
              :class="scope.row.isProcessed ? 'c-activity-badge--status-completed' : 'c-activity-badge--status-pending'"
            >
              {{ scope.row.isProcessed ? 'Yes' : 'No' }}
            </span>
          </template>
        </el-table-column>
        <template #empty>
          <section class="c-no-results">
            <el-icon :size="80">
              <Search />
            </el-icon>
            <h2 class="text-black">No results found</h2>
            <h6 class="text-black-50 fw-light">Try changing the filters or search terms for this view</h6>
            <el-button @click="deleteSearchAndFilters" size="large" type="success">View all transactions</el-button>
          </section>
        </template>
      </el-table>
      <el-pagination
        v-model:current-page="currentPage"
        :page-size="rowsPerPage"
        :pager-count="11"
        :total="totalRows"
        class="ac-pagination text-center mt-4"
        layout="prev, pager, next"
        size="large"
        @current-change="handlePageChange"
        @size-change="handlePageSizeChange" />
    </template>
  </section>

  <ActivityDetails :transaction-id="detailsTransactionId" v-if="showDetails" v-model="showDetails" />
</template>

<style lang="scss" scoped>
// Color Variables - Match Dashboard.vue
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-300: #d1d5db;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;
$color-teal-accent: #01beb0;
$color-purple-text: #4349dd;

.c-activity-table {
  position: relative;
  z-index: 0;
  width: 100%;
  overflow-x: auto;

  :deep(.el-table) {
    border-radius: 0;
    min-width: 100%;
    --el-table-border-color: rgba(0, 0, 0, 0.1);
    --el-table-header-bg-color: white;
    --el-table-row-hover-bg-color: #{$gray-50};

    .cell {
      color: #0a0a0a;
      font-weight: 400;
      font-size: 0.875rem;
      line-height: 1.25rem;
      white-space: nowrap;
      padding: 0 0.5rem;

      // Allow multiline for specific columns
      &:has(.c-activity-table__details) {
        white-space: normal;
      }
    }

    th > .cell {
      text-transform: none;
      color: #0a0a0a;
      font-weight: 500;
      font-size: 0.875rem;
      letter-spacing: 0;
      padding: 0 0.5rem;
    }

    th.el-table__cell.is-leaf {
      background-color: white;
      height: 40px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.1);
      padding: 0;
    }

    td.el-table__cell {
      border-bottom: 1px solid rgba(0, 0, 0, 0.1);
      padding: 0.5rem 0;
    }

    .el-table__row {
      cursor: pointer;
      transition: background-color 0.15s ease;

      &:last-child td.el-table__cell {
        border-bottom: none;
      }
    }

    .el-table__empty-block {
      min-height: 400px;
    }

    // Enable horizontal scrolling
    .el-table__body-wrapper {
      overflow-x: auto;
    }
  }

  &__transaction-id {
    font-weight: 400;
    color: #0a0a0a;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
      'Courier New', monospace;
    font-size: 0.875rem;
  }

  &__item {
    font-weight: 500;
    color: #0a0a0a;
    max-width: 160px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__user {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__avatar {
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background-color: #ececf0;
    color: #0a0a0a;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 400;
    flex-shrink: 0;
    text-transform: uppercase;
  }

  &__user-name {
    font-weight: 500;
    color: #0a0a0a;
    font-size: 0.875rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__date {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 400;
    color: $gray-600;
  }

  &__date-icon {
    font-size: 0.875rem;
    color: #9ca3af;
    flex-shrink: 0;
  }

  &__amount {
    font-weight: 600;
    color: $gray-900;

    &--zero {
      color: #9ca3af;
    }
  }

  &__details {
    white-space: normal;
    word-break: normal;
    overflow-wrap: break-word;
    line-height: 1.5;
    max-width: 260px;
    font-size: 0.75rem;
    line-height: 1rem;
    color: $gray-500;
  }

  &__shop-link {
    font-weight: 600;
    color: $color-purple-text;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &__shop-source {
    font-weight: 500;
    color: $gray-600;
    font-style: italic;
  }

  &__select-all {
    padding: 0 1.5rem;
    position: absolute;
    z-index: 10;
    width: 100%;
    background: white;
    align-items: center;
    display: flex;
    gap: 1.25rem;
    height: auto;
    border-bottom: 1px solid $gray-100;
    transition: all 0.3s ease;

    &--small {
      overflow: hidden;
      width: 40px;
      padding: 0 0.75rem;
    }

    .fs-12 {
      font-size: 0.75rem;
      font-weight: 500;
      color: $gray-600;
    }

    .fs-10 {
      font-size: 0.625rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    :deep(.el-link) {
      &.text-success {
        color: $color-teal-accent;
        font-weight: 700;

        &:hover {
          color: color.adjust($color-teal-accent, $lightness: -10%);
        }
      }
    }
  }

  .c-no-results {
    margin-top: 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    gap: 1rem;
    padding: 2rem;

    h2 {
      font-size: 1.25rem;
      font-weight: 700;
      color: $gray-900;
      margin: 0;
    }

    h6 {
      font-size: 0.875rem;
      font-weight: 400;
      color: $gray-500;
      margin: 0;
      text-align: center;
    }

    :deep(.el-icon) {
      color: $gray-300;
    }

    :deep(.el-button) {
      margin-top: 0.5rem;
    }
  }

  // Skeleton loader styles
  .c-skeleton-loader {
    width: 100%;
    padding: 1rem 0;
  }

  .c-skeleton-row {
    display: flex;
    align-items: center;
    padding: 1rem 1.5rem;
    gap: 1rem;
    border-bottom: 1px solid $gray-100;

    &:last-child {
      border-bottom: none;
    }
  }

  .c-skeleton-cell {
    height: 20px;
    background: linear-gradient(90deg, #F0F0F0 25%, #E0E0E0 50%, #F0F0F0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 4px;

    &--checkbox {
      width: 20px;
      height: 20px;
      flex-shrink: 0;
    }

    &--transaction {
      width: 100px;
      flex-shrink: 0;
    }

    &--date {
      width: 120px;
      flex-shrink: 0;
    }

    &--user {
      width: 150px;
      flex-shrink: 0;
    }

    &--email {
      width: 180px;
      flex-shrink: 0;
    }

    &--amount {
      width: 80px;
      flex-shrink: 0;
    }

    &--item {
      width: 140px;
      flex-shrink: 0;
    }

    &--details {
      width: 200px;
      flex-shrink: 0;
    }

    &--type {
      width: 100px;
      flex-shrink: 0;
    }

    &--anonymous {
      width: 60px;
      flex-shrink: 0;
    }
  }

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  .error-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    padding: 2rem;
    gap: 1rem;

    .error-icon {
      font-size: 4rem;
      color: #f43300;
    }

    .error-text {
      text-align: center;

      h2 {
        font-size: 1.5rem;
        font-weight: 700;
        color: $gray-900;
        margin: 0 0 0.5rem 0;
      }

      p {
        font-size: 0.875rem;
        color: $gray-500;
        margin: 0;
        line-height: 1.5;
      }
    }
  }
}

// Badge Styles (shadcn outline Badge)
.c-activity-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.125rem 0.5rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1rem;
  white-space: nowrap;
  border: 1px solid;
  background-color: white;

  &--reward {
    color: #ea580c;
    border-color: #f97316;
  }

  &--payment {
    color: #005c6b;
    border-color: #01beb0;
  }

  &--peer-transfer {
    color: #4349dd;
    border-color: #4349dd;
  }

  &--default {
    color: #005c6b;
    border-color: #01beb0;
  }

  &--status-completed {
    background-color: #dde9f9;
    color: #4876c9;
    border-color: #6ba4f0;
  }

  &--status-pending {
    background-color: $gray-100;
    color: $gray-600;
    border-color: $gray-300;
  }

  &--anonymous {
    background-color: $gray-50;
    color: $gray-700;
    border-color: #e5e7eb;
  }

  &--none {
    border-color: transparent;
    background-color: transparent;
    font-weight: 400;
    font-size: 0.875rem;
    color: #9ca3af;
  }
}

// Pagination Styling
:deep(.ac-pagination) {
  display: flex;
  justify-content: center;
  padding: 1.5rem;

  .el-pager {
    li {
      border-radius: 0.375rem;
      font-weight: 500;
      transition: all 0.15s ease;
      background-color: transparent;
      border: 1px solid $gray-300;
      color: $gray-700;

      &.is-active {
        background-color: transparent;
        color: $color-purple-text;
        border-color: $color-purple-text;
        font-weight: 600;
      }

      &:hover:not(.is-active) {
        background-color: transparent;
        border-color: $gray-500;
      }
    }
  }

  .btn-prev,
  .btn-next {
    border-radius: 0.375rem;
    font-weight: 500;
    transition: all 0.15s ease;
    background-color: transparent;
    border: 1px solid $gray-300;
    color: $gray-700;

    &:hover:not(:disabled) {
      background-color: transparent;
      border-color: $gray-500;
    }

    &:disabled {
      border-color: $gray-300;
      opacity: 0.5;
    }
  }
}

// Responsive Design
@media (max-width: 768px) {
  .c-activity-table {
    :deep(.el-table) {
      .cell {
        font-size: 0.8125rem;
      }
    }

    &__select-all {
      padding: 0 1rem;
      gap: 0.75rem;

      .fs-12 {
        font-size: 0.6875rem;
      }
    }
  }
}
</style>
